import React, { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { isStoreCheckoutEditPage } from '../helpers/storeCheckoutStoreDetails.utils';
import { useStyleClass } from '@/hooks/useStyleClass';
import { Text } from '@/components/Text/Text';
import Input from '@/components/Input/Input';
import Icon from '@/components/Icon/Icon';
import PaymentLogos from '@/components/PaymentLogos/PaymentLogos';
import { getClientAccountLabel, getDueDateFromCurrentDate } from '@/utilities/CreateOrderUtils';
import { validateEmail } from '@/components/utils/FunctionUtils';
import './SelectPaymentOption.scss';

interface SelectPaymentOptionProps {
  storeType?: 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | '';
}

const SelectPaymentOption: React.FC<SelectPaymentOptionProps> = ({ storeType }) => {
  const styleClass = useStyleClass('store-review-order-step', true);
  const { watch, setValue, control } = useFormContext();

  const isEditPage = isStoreCheckoutEditPage();

  const selectedPaymentOption = watch('paymentOption');
  const isPayLater = selectedPaymentOption === 'invoice';

  const paymentOptions = useMemo(
    () => [
      {
        value: 'checkout',
        title: 'Pay via Checkout',
        description: 'Participants will pay directly while ordering.',
      },
      {
        value: 'invoice',
        title: 'Pay Later',
        description: 'We will invoice your account and you will have 30 days to pay.',
      },
    ],
    []
  );

  // For permanent stores, show info text instead of payment options
  if (storeType === 'permanent-store') {
    return (
      <div className={styleClass}>
        <div className={`${styleClass}__wrapper`}>
          <div style={{ margin: 0, lineHeight: '1.5' }}>
            <Text as="p" size={14} color="#474B6D">
              Permanent Stores only support individual checkout. Each customer will pay directly
              when they place their order.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styleClass}>
      <div className={`${styleClass}__wrapper`}>
        <div className={`${styleClass}__pricing-models store-payment-options`}>
          {paymentOptions.map((option) => (
            <div
              key={option.value}
              className={`${styleClass}__pricing-model-card ${
                selectedPaymentOption === option.value
                  ? `${styleClass}__pricing-model-card--selected`
                  : ''
              } ${isEditPage ? `${styleClass}__pricing-model-card--disabled` : ''}`}
              onClick={() => !isEditPage && setValue('paymentOption', option.value)}
              role="button"
              tabIndex={isEditPage ? -1 : 0}
              onKeyDown={(e) => {
                if (!isEditPage && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setValue('paymentOption', option.value);
                }
              }}
              style={isEditPage ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
            >
              <div className={`${styleClass}__pricing-model-radio-container`}>
                <input
                  type="radio"
                  name="paymentOption"
                  checked={selectedPaymentOption === option.value}
                  onChange={() => !isEditPage && setValue('paymentOption', option.value)}
                  id={`payment-${option.value}`}
                  disabled={isEditPage}
                />
                <label htmlFor={`payment-${option.value}`} />
              </div>
              <div
                className={`${styleClass}__store-pricing-option-content store-pricing-option-content`}
              >
                <Text
                  as="div"
                  size={16}
                  weight="bold"
                  color={selectedPaymentOption === option.value ? '#4A90E2' : '#474B6D'}
                  className={`${styleClass}__pricing-model-title`}
                >
                  {option.title}
                </Text>
                <Text
                  as="div"
                  size={14}
                  color="#8D90AA"
                  className={`${styleClass}__pricing-model-description`}
                >
                  {option.description}
                </Text>
              </div>
            </div>
          ))}
        </div>

        {/* Pay Later Section - Show when invoice option is selected */}
        {isPayLater && (
          <div className={`${styleClass}__pay-later`} style={{ marginTop: '24px' }}>
            <div
              style={{
                marginBottom: '16px',
                fontSize: '14px',
                color: '#474B6D',
                lineHeight: '1.5',
              }}
            >
              Need to pay by check, venmo, or member funds? We will process your order and send{' '}
              <strong>{getClientAccountLabel()}</strong> an invoice under your name. The invoice
              will be due on <strong>{getDueDateFromCurrentDate(30)}</strong>.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <PaymentLogos
                showSecureLogo={false}
                showGooglePayLogo={false}
                showPaypalLogo={false}
              />
            </div>
            <div style={{ marginTop: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#474B6D',
                    marginBottom: '12px',
                  }}
                >
                  Send Invoice To
                </div>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      name="ccEmail"
                      label="Email"
                      type="email"
                      containerStyle={{
                        width: '300px',
                      }}
                      contentBefore={
                        <Icon
                          className="cc-email-icon"
                          source="/src/assets/svg/accounts/email.svg"
                          height="15px"
                          width="20px"
                        />
                      }
                      fieldErrMessg={error?.message ?? ''}
                      floatingLabel
                      disabled={isEditPage}
                    />
                  )}
                  name="ccEmail"
                  control={control}
                  rules={{
                    validate: (value: string) =>
                      !value ||
                      validateEmail(value) ||
                      'Email entered is invalid. Please enter a valid one.',
                  }}
                />
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    marginTop: '8px',
                    lineHeight: '1.5',
                  }}
                >
                  We will send a copy with a button that can be used to pay.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Controller
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return (
                      <input
                        {...rest}
                        type="checkbox"
                        checked={field.value}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: isEditPage ? 'not-allowed' : 'pointer',
                          flexShrink: 0,
                        }}
                        disabled={isEditPage}
                      />
                    );
                  }}
                  name="canChargeAccount"
                  control={control}
                  rules={{
                    validate: (value: boolean) => value,
                  }}
                />
                <label
                  onClick={() => {
                    if (isEditPage) {
                      return;
                    }

                    const value = watch('canChargeAccount');
                    setValue('canChargeAccount', !value);
                  }}
                  style={{
                    fontSize: '14px',
                    color: '#474B6D',
                    cursor: isEditPage ? 'not-allowed' : 'pointer',
                    lineHeight: '1.5',
                  }}
                >
                  I am authorized to charge {getClientAccountLabel('my account')} for this order. *
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SelectPaymentOption.displayName = 'SelectPaymentOption';

export default SelectPaymentOption;
